<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 bg-black-transparent flex items-center justify-center" style="z-index: 10001" @click.self="close">
      <div class="bg-white p-32 rounded-regular shadow-lg w-full mx-24 max-w-600">
        <h3 class="text-heading-small mb-24">{{ $lang('account', 'reset_password') }}</h3>
        <form @submit.prevent="handleSubmit" class="space-y-16">
          <div>
            <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'email') }}</label>
            <TextInput 
              v-model="email" 
              type="email" 
              required 
            />
          </div>
          <div v-if="error" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
            {{ error }}
          </div>
          <div v-if="success" class="text-success text-sm bg-green-25 px-12 py-8 rounded-regular">
            {{ $lang('account', 'reset_email_sent') }}
          </div>
          <div class="flex gap-12 pt-8">
            <button 
              type="button"
              class="btn btn--md btn--secondary flex-1"
              @click="close"
            >
              {{ $lang('account', 'cancel') }}
            </button>
            <button 
              type="submit" 
              class="btn btn--md btn--primary flex-1"
              :disabled="loading"
            >
              {{ loading ? $lang('account', 'sending') : $lang('account', 'send_reset_link') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import TextInput from '~/components/forms/TextInput.vue';

const props = defineProps<{
  modelValue: boolean;
  initialEmail?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { $lang } = useNuxtApp();
const route = useRoute();

const locale = computed(() => {
  const pathSegments = route.path.split('/').filter(Boolean);
  return pathSegments[0] || 'se';
});

const email = ref('');
const error = ref('');
const success = ref(false);
const loading = ref(false);

// Watch for modal opening and pre-fill email if provided
watch(() => props.modelValue, (isOpen) => {
  if (isOpen && props.initialEmail) {
    email.value = props.initialEmail;
  }
});

function close() {
  emit('update:modelValue', false);
  // Reset state after a short delay to allow transition
  setTimeout(() => {
    email.value = '';
    error.value = '';
    success.value = false;
  }, 300);
}

async function handleSubmit() {
  error.value = '';
  success.value = false;
  loading.value = true;
  
  try {
    await $fetch('/api/centra/forgot-password', {
      method: 'POST',
      body: { 
        email: email.value,
        locale: locale.value,
      },
    });
    success.value = true;
    setTimeout(() => {
      close();
    }, 3000);
  } catch (err: any) {
    console.error('Forgot password error:', err);
    error.value = err.data?.message || $lang('account', 'failed_send_reset_email');
  } finally {
    loading.value = false;
  }
}
</script>
